import DeploymentStageValues from "./DeploymentStageValues";
import { useId, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./PromotionArtifactDeployments.css";

// Stored intent does not prove runtime deployment. Missing values stay unknown.
export default function PromotionArtifactDeployments({ artifacts = [], artifactIds = [], designArtifacts = [] }) {
  const { t } = useTranslation("freigabe");
  const descriptionId = useId();
  const [paging, setPaging] = useState({ key: "", page: 0, rows: 20 });
  const records = Array.isArray(artifacts) ? artifacts : [];
  const validId = value => typeof value === "string" && value.trim().length > 0;
  const byId = new Map(records.filter(record => validId(record?.id)).map(record => [record.id, record]));
  const ids = [...new Set([...(Array.isArray(artifactIds) ? artifactIds : []), ...byId.keys()].filter(validId))];
  // v27: Designobjekte mit Edge-Vormerkung (defensiv: Feld kann fehlen).
  const designRecords = Array.isArray(designArtifacts) ? designArtifacts.filter(item => item && validId(item.id)) : [];
  const allIds = [...ids, ...designRecords.map(item => item.id)];
  const key = JSON.stringify(allIds);
  const rows = paging.rows;
  const page = Math.min(paging.key === key ? paging.page : 0, Math.max(0, Math.ceil(allIds.length / rows) - 1));
  if (!allIds.length) return null;
  const designById = new Map(designRecords.map(item => [item.id, item]));
  return <div className="promotion-artifact-deployments">
    <p id={descriptionId} className="promotion-artifact-table-hint">{t("deployment.intentHint")}</p>
    <TableContainer className="promotion-artifact-table-scroll" tabIndex={0} role="region" aria-label={t("deployment.tableLabel")}>
      <Table stickyHeader size="small" className="promotion-artifact-table" aria-label={t("promotion.artifacts")} aria-describedby={descriptionId}>
        <TableHead><TableRow>
          <TableCell scope="col">{t("deployment.name")}</TableCell>
          <TableCell scope="col">{t("deployment.id")}</TableCell>
          <TableCell scope="col">{t("deployment.ci")}</TableCell>
          <TableCell scope="col">{t("deployment.edge")}</TableCell>
        </TableRow></TableHead>
        <TableBody>{allIds.slice(page * rows, (page + 1) * rows).map(id => {
          const artifact = byId.get(id);
          const design = designById.get(id);
          if (design) {
            // Gleiche Form wie bei iFlows: deployment.edgeManual, je Stufe deploymentByStage[].edgeManual.
            const edgeValue = typeof design.deployment?.edgeManual === "boolean" ? design.deployment.edgeManual : null;
            return <TableRow key={`design:${id}`}>
              <TableCell component="th" scope="row" data-label={t("deployment.name")}>{design.name || id}{design.type ? ` (${design.type})` : ""}</TableCell>
              <TableCell data-label={t("deployment.id")}>{id}</TableCell>
              <TableCell data-label={t("deployment.ci")}></TableCell>
              <TableCell data-label={t("deployment.edge")}><DeploymentStageValues plans={design.deploymentByStage} field="edgeManual" fallback={edgeValue}/></TableCell>
            </TableRow>;
          }
          return <TableRow key={id}>
            <TableCell component="th" scope="row" data-label={t("deployment.name")}>{artifact?.name || t("deployment.unknown")}</TableCell>
            <TableCell data-label={t("deployment.id")}>{id}</TableCell>
            <TableCell data-label={t("deployment.ci")}><DeploymentStageValues plans={artifact?.deploymentByStage} field="ci" fallback={artifact?.deployment?.ci}/></TableCell>
            <TableCell data-label={t("deployment.edge")}><DeploymentStageValues plans={artifact?.deploymentByStage} field="edgeManual" fallback={artifact?.deployment?.edgeManual}/></TableCell>
          </TableRow>;
        })}</TableBody>
      </Table>
    </TableContainer>
    {allIds.length > 20 && <TablePagination component="div" className="promotion-pagination promotion-artifact-pagination" count={allIds.length}
      page={page} rowsPerPage={rows} rowsPerPageOptions={[20, 50, 100]}
      onPageChange={(_, value) => setPaging({ key, page: value, rows })}
      onRowsPerPageChange={event => setPaging({ key, page: 0, rows: Number(event.target.value) })}
      labelRowsPerPage={t("deployment.rowsPerPage")} labelDisplayedRows={({ from, to, count }) => t("topics.pageRange", { from, to, count })}
      getItemAriaLabel={type => t(`topics.page.${type}`)}/>}
  </div>;
}
